import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Segmented, Space, Tooltip, Typography, message } from 'antd';
import { ExclamationCircleOutlined, FlagOutlined, ReloadOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import { applyReviewChange, pvpReviewBody, ruleText } from '@/utils/pvpReview';
import { PVP_REVIEW_DEFAULTS } from '@/utils/pvpSettings';
import type { PvpReviewList, PvpReviewPlayer, PvpReviewStatus } from '@/types/pvp';
import { FlagPlayerModal, type FlagTarget } from './FlagPlayerModal';
import { ReviewTable } from './ReviewTable';

const { Title, Text } = Typography;
const { confirm } = Modal;

/** The most the review endpoint lists in one call. */
const LIST_LIMIT = 200;
const SHOW_ONLY = 'You can only view players (show-only access, or the module is read-only).';

const STATUSES: Array<{ label: string; value: PvpReviewStatus }> = [
  { label: 'Flagged now', value: 'flagged' },
  { label: 'Everyone past the minimum balls', value: 'all' },
];

/**
 * Realtime PvP (Rivals) cheat review: players the ledger flagged because their
 * batting since the last clear broke the review rule, and players an admin
 * flagged. A flag makes their matches free; clearing it restarts the counters.
 */
export const PvpReviewPage: React.FC = () => {
  const [status, setStatus] = useState<PvpReviewStatus>('flagged');
  const [data, setData] = useState<PvpReviewList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagTarget, setFlagTarget] = useState<FlagTarget | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const loadGuard = useRef(createLatestGuard());
  const { data: canEdit } = useCan({ resource: 'players', action: 'edit' });
  const canChange = canEdit?.can !== false;
  const deniedTitle = canChange ? undefined : SHOW_ONLY;

  const load = useCallback(async () => {
    const isLatest = loadGuard.current.begin();
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.pvp.review({ status, limit: LIST_LIMIT });
      if (!isLatest()) return;
      setData(response.data);
    } catch (e) {
      if (!isLatest()) return;
      setError(apiErrorMessage(e, 'Failed to load the PvP review list'));
      setData(null);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  // Apply the PUT answer (read on the primary) rather than reloading from the
  // replica, which can still show the old flag for a moment.
  const applyChange = (player: PvpReviewPlayer) =>
    setData((current) => (current ? applyReviewChange(current, status, player) : current));

  const clear = (player: PvpReviewPlayer) => {
    const name = player.displayName ?? player.userId;
    confirm({
      title: `Clear the PvP flag for ${name}?`,
      icon: <ExclamationCircleOutlined />,
      content:
        'Their matches cost the normal fee again from the next match they are paired in. Their review counters restart from now: the balls so far no longer count towards the rule.',
      okText: 'Clear flag',
      onOk: async () => {
        setBusyIds((ids) => new Set(ids).add(player.userId));
        try {
          const response = await adminApi.pvp.setReview(player.userId, pvpReviewBody(false));
          message.success(`Flag cleared for ${name}`);
          applyChange(response.data);
        } catch (e) {
          message.error(apiErrorMessage(e, 'Failed to clear the flag'));
        } finally {
          setBusyIds((ids) => {
            const next = new Set(ids);
            next.delete(player.userId);
            return next;
          });
        }
      },
    });
  };

  const items = data?.items ?? [];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        PvP Review
      </Title>
      <Text type="secondary">
        Realtime PvP (Rivals) players flagged for a possible modified client: by the review rule, or
        by an admin.
      </Text>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message="A flag is never a ban"
        description={
          <>
            A flagged player keeps playing PvP, but free: entry fee 0 and 0 paid matches left, from
            the next match they are paired in (a match already found keeps its fee). Their
            opponent's match is free too, since a match costs the lower of the two fees. Coins
            already won stay, and their rating still counts. Clearing a flag restarts their review
            counters from now. While the entry fee is 0 every match is free anyway, so a flag
            changes nothing until a fee is set.
          </>
        }
      />

      {data && (
        <Text style={{ display: 'block', marginTop: 12 }}>
          The rule flags a batter {ruleText(data.rule)}. Change it in{' '}
          <Link to="/config?tab=pvp">Configuration → Realtime PvP</Link>.
        </Text>
      )}

      <Space wrap style={{ margin: '12px 0', width: '100%', justifyContent: 'space-between' }}>
        <Segmented
          options={STATUSES}
          value={status}
          onChange={(v) => {
            // The other segment's rows must not show under the spinner.
            setData(null);
            setStatus(v as PvpReviewStatus);
          }}
        />
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Tooltip title={deniedTitle}>
            <Button
              danger
              icon={<FlagOutlined />}
              disabled={!canChange}
              onClick={() => setFlagTarget({ byId: true })}
            >
              Flag a player by id
            </Button>
          </Tooltip>
        </Space>
      </Space>

      {status === 'all' && data && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
          Everyone with at least {data.rule.minBalls} balls since their last clear, flagged or not,
          highest runs a ball first.
        </Text>
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {/* After a failed load the alert says why; an empty table would say "nobody to review". */}
      {(data || !error) && (
        <ReviewTable
          items={items}
          // No rows until the list has loaded, so the defaults are never judged against.
          rule={data?.rule ?? PVP_REVIEW_DEFAULTS}
          loading={loading}
          canChange={canChange}
          deniedTitle={deniedTitle}
          busyIds={busyIds}
          onFlag={(player) => setFlagTarget({ player })}
          onClear={clear}
        />
      )}
      {items.length >= LIST_LIMIT && (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
          Showing the first {LIST_LIMIT}.
        </Text>
      )}

      <FlagPlayerModal
        target={flagTarget}
        onClose={() => setFlagTarget(null)}
        onFlagged={applyChange}
      />
    </div>
  );
};

export default PvpReviewPage;
